/* eslint-disable no-alert */
import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
// @mui
import { Box } from '@mui/material';
// hooks
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import PieSocket from 'piesocket-js';
import { refreshThreadMessages, resetChatData } from 'src/redux/slices/whatsapp';
import { setPersistedWpNotifications, shownWhatsappCallToastIds, shownWpToastIds } from 'src/redux/slices/liveNotificationSlice';
import { fetchMergeTagKeys, fetchMergeTagValueForPractice, fetchMergeTagValueForUser, fetchPracticeUsers } from 'src/redux/slices/mergeTags';
import { getAllTagItem } from 'src/redux/slices/tagsCreate';
import { selectLeads } from 'src/redux/slices/lead';
import { applyMessageEdited, applyMessagePinned, applyMessageReaction, applyMessageRevoked, applyMessageStarred, applyMessageUnpinned, applyMessageUnstarred, handleAddWhatsappNotificationCount, initializeWhatsapp, receiveWhatsappCallRecordingUpdate, receiveWhatsappCallTranscriptSegment, receiveWhatsappCallUpdate, refreshThreadAndMessageInfo, updateSubChannel,
    receiveWhatsappAiStatus,
} from 'src/redux/slices/whatsappV2';
import { scheduleFCMRegistration, listenForForegroundMessages } from 'src/fcmService';
import { prefetchGoogleReviewsOnDashboardLoad } from 'src/redux/slices/reviewStatsCache';
import { rememberLastPractice, userKeyOf } from 'src/utils/lastPractice';
import { getPracticeTablePreferences } from 'src/redux/slices/tablePreferences';
import { useNotifications } from '../../sections/notifications/NotificationProvider';
import { PATH_DASHBOARD } from '../../routes/paths';
import { useAuth, useResponsive } from '../../hooks';
import useEnsurePracticeDetail from '../../hooks/useEnsurePracticeDetail';
import { useSettingsContext } from '../../components/settings/context';
import Main from './Main';
import DashboardHeader from './header';
import NavbarVertical from './navbar/NavbarVertical';
import NavbarHorizontal from './navbar/NavbarHorizontal';
import NavMini from './navbar/NavMini';
import EnableNotificationsDialog from './popups/EnableNotificationsDialog';
import CommandPalette from '../../components/CommandPalette';
import CreateLeadDialogWrapper from '../../components/CreateLeadDialogWrapper';
import ContactLeadPopupSingleton from '../../components/ContactLeadPopupSingleton';
import TaskUndoSnackbarHost from '../../sections/tasks/TaskUndoSnackbarHost';
import AttendanceCaptureHost from '../../components/attendance/AttendanceCaptureHost.tsx';
import { newAttendanceOperationKey, attendanceScopeForUser } from '../../hooks/useAttendanceCapture.ts';
import TwilioVoiceProvider from '../../sections/twilioVoice/TwilioVoiceProvider';
import { pieRoom } from '../../utils/piesocketRoom';

const INCOMING_WHATSAPP_CALL_STATUSES = new Set(['pending', 'connecting', 'ringing']);

function showCustomNotification() {
    try {
        // Check for notification permission first
        if (Notification.permission === "granted") {
            // this is triggered from service worker for system notification
        } else if (Notification.permission === "default") {
            // If it's not denied, we can ask for permission
            Notification.requestPermission().then(permission => {
                if (permission === "granted") {
                    new Notification("Permission Granted!", {
                        body: "You will now recieve notifications like this!",
                        icon: "/favicon/android-chrome-512x512.png"
                    });
                }
            });
        }
    } catch (error) {
        console.error("Error in OS Notif", error)
    }
}

export default function DashboardLayout() {
    const { themeLayout } = useSettingsContext();
    const dispatch = useDispatch();
    const { user } = useAuth();
    const [attendanceSessionKey] = React.useState(newAttendanceOperationKey);
    const {
        currentPractice,
        practiceSwitchEpoch,
        whatsappJwtTokens,
        businessIdToPhoneMap,
        permissionsLoading,
        permissionsPracticeId,
        whatsappMessagingPermissionLevel,
        whatsappIsLoading,
        whatsappInitializedForPracticeId,
    } = useSelector(
        (state) => ({
            jwtToken: state?.whatsApp?.jwtToken,
            practiceMetaData: state?.whatsApp?.practiceMetaData,
            currentPractice: state.practiceTreatmentList?.practiceId,
            practiceSwitchEpoch: state.practiceTreatmentList?.practiceSwitchEpoch,
            whatsappJwtTokens: state.whatsappV2?.whatsappJwtTokens,
            businessIdToPhoneMap: state.whatsappV2?.businessIdToPhoneMap,
            permissionsLoading: state.permissions?.loading,
            permissionsPracticeId: state.permissions?.practiceId,
            whatsappMessagingPermissionLevel: state.permissions?.permissions?.['whatsapp-messaging'],
            whatsappIsLoading: state.whatsappV2?.isLoading,
            whatsappInitializedForPracticeId: state.whatsappV2?.initializedForPracticeId,
        }),
        shallowEqual
    );
    // Every dashboard route needs the active practice's full record (preferences
    // drive the attendance-capture and Follow-Up V2 gates); see the hook's note.
    useEnsurePracticeDetail(currentPractice);
    const { showNotification } = useNotifications();
    const showNotificationRef = React.useRef(showNotification);
    const shownCallToastIdsRef = React.useRef(new Set());
    const whatsappInitAttemptedPracticeRef = React.useRef(null);
    showNotificationRef.current = showNotification;
    const isDesktop = useResponsive('up', 'lg');
    const [open, setOpen] = useState(false);
    const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
    const horizontalLayout = themeLayout === 'horizontal';
    const isNavMini = themeLayout === 'mini';

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const renderNavVertical = <NavbarVertical openNav={open} onCloseNav={handleClose} />;

    React.useEffect(() => {
        // Track connections created in this effect run so the cleanup can
        // unsubscribe exactly the ones it subscribed — not older or newer ones.
        // `isCancelled` guards the async `.then()` path: if cleanup runs before
        // the subscribe resolves, the callback detects cancellation and
        // immediately tears down the newly-established connection.
        let isCancelled = false;
        const localSubs = [];

        const jwtArray = Object.keys(whatsappJwtTokens ?? {}).flatMap(businessId =>
            Object.keys(whatsappJwtTokens?.[businessId] ?? {}).map(phoneId => {
                const phoneNumber = businessIdToPhoneMap?.[businessId]?.filter(phoneOb => `${phoneOb?.id}` === `${phoneId}`)?.[0]?.phone_number_id ?? '';
                return ({
                    phoneId,
                    businessId,
                    jwtToken: whatsappJwtTokens?.[businessId]?.[phoneId] ?? '',
                    roomId: pieRoom(`${currentPractice}_${phoneNumber}`),
                    phoneNumber
                })
            })
        );

        jwtArray?.forEach(jwtObj => {
            if (jwtObj?.jwtToken && jwtObj?.phoneNumber && jwtObj?.roomId) {
                const pieSocketConn = new PieSocket({
                    clusterId: import.meta.env.VITE_REACT_APP_SOCKET_CLUSTER_KEY || '',
                    apiKey: import.meta.env.VITE_REACT_APP_SOCKET_API_KEY || '',
                    jwt: jwtObj.jwtToken,
                });
                pieSocketConn.subscribe(jwtObj.roomId).then(channel => {
                    // Effect was cleaned up before this async callback fired
                    // (e.g. rapid practice-switch or logout). Tear down immediately.
                    if (isCancelled) {
                        try { pieSocketConn.unsubscribe(jwtObj.roomId); } catch (_) { /* ignore */ }
                        return;
                    }
                    const listener = (data) => {
                        if (jwtObj.phoneNumber !== data?.from) {
                            dispatch(setPersistedWpNotifications(data));
                            dispatch(handleAddWhatsappNotificationCount());

                            // Show WhatsApp toast from the phone-specific channel.
                            // This is the reliable path — the platform _notifications channel
                            // may not always deliver WhatsApp events.
                            const wpMsgId = data?.id || data?.wamid || '';
                            if (!wpMsgId || !shownWpToastIds.has(wpMsgId)) {
                                if (wpMsgId) shownWpToastIds.add(wpMsgId);
                                const threadId = data?.thread || data?.threadId || data?.thread_id;
                                showNotificationRef.current({
                                    id: wpMsgId || `wp_phone_${Date.now()}`,
                                    verb: 'whatsapp_message_received',
                                    description: data?.name
                                        ? `WhatsApp from ${data.name}: ${data.body || ''}`
                                        : (data?.body || 'New WhatsApp message'),
                                    priority: 'normal',
                                    requires_action: true,
                                    action_label: 'Open Chat',
                                    action_link: threadId && currentPractice
                                        ? PATH_DASHBOARD.whatsapp.thread(currentPractice, threadId)
                                        : undefined,
                                    data: { threadId },
                                });
                            }
                        }
                        // background: the inbox counts refresh at most once
                        // per 10 s for a burst of messages (CU-869fn2znc).
                        dispatch(refreshThreadAndMessageInfo(data?.thread, undefined, true, false, false, { background: true }));
                        dispatch(refreshThreadMessages(data?.thread || -1, true, true));
                    };

                    channel.listen('new-whatsapp-message', listener);

                    channel.listen('whatsapp-call-updated', (data) => {
                        const call = data?.data ?? data;
                        dispatch(receiveWhatsappCallUpdate(call));

                        const callId = call?.id || call?.call_id || call?.client_call_id;
                        const isInbound = call?.direction === 'inbound';
                        const status = String(call?.status || '').toLowerCase();
                        const shouldNotify = (
                            callId
                            && isInbound
                            && INCOMING_WHATSAPP_CALL_STATUSES.has(status)
                            && !shownCallToastIdsRef.current.has(String(callId))
                            && !shownWhatsappCallToastIds.has(String(callId))
                        );
                        if (!shouldNotify) return;

                        shownCallToastIdsRef.current.add(String(callId));
                        shownWhatsappCallToastIds.add(String(callId));
                        const threadId = call?.thread || call?.threadId || call?.thread_id;
                        let actionLink;
                        if (currentPractice && threadId) {
                            actionLink = PATH_DASHBOARD.whatsapp.thread(currentPractice, threadId);
                        } else if (currentPractice) {
                            actionLink = PATH_DASHBOARD.whatsapp.root(currentPractice);
                        }
                        showNotificationRef.current({
                            id: `whatsapp_call_${callId}`,
                            verb: 'whatsapp_call_incoming',
                            description: `Incoming WhatsApp call from ${call?.peer_waid || 'patient'}`,
                            priority: 'urgent',
                            requires_action: true,
                            action_label: 'Open Call',
                            action_link: actionLink,
                            data: { callId, threadId },
                        });
                    });
                    // The AI's transient working state on a thread. Rides the same
                    // channel as the messages themselves, so anyone who can see the
                    // conversation sees that a reply is on its way — which is what
                    // lets a colleague decide whether to answer it first.
                    channel.listen('whatsapp-ai-status', (data) => {
                        dispatch(receiveWhatsappAiStatus(data?.data ?? data));
                    });
                    channel.listen('whatsapp-call-recording-updated', (data) => {
                        dispatch(receiveWhatsappCallRecordingUpdate(data?.data ?? data));
                    });
                    channel.listen('whatsapp-call-transcript', (data) => {
                        dispatch(receiveWhatsappCallTranscriptSegment(data?.data ?? data));
                    });

                    const reactionListener = (data) => {
                        const messageId = data?.message_id ?? data?.id;
                        if (messageId == null) return;
                        dispatch(applyMessageReaction({
                            messageId,
                            emoji: data?.emoji ?? null,
                            removed: Boolean(data?.removed),
                            byUser: data?.by_user ?? null,
                        }));
                    };
                    channel.listen('message-reaction', reactionListener);

                    const revokedListener = (data) => {
                        const messageId = data?.message_id ?? data?.id;
                        if (messageId == null) return;
                        dispatch(applyMessageRevoked({ messageId }));
                    };
                    channel.listen('message-revoked', revokedListener);

                    // Track B — Star + Pin live updates. Each handler is a
                    // tiny dispatch; the events are practice-wide so any
                    // operator's action propagates to every connected
                    // client. message_id / thread_id arrive flat on data.
                    channel.listen('message-starred', (data) => {
                        dispatch(applyMessageStarred({
                            messageId: data?.message_id,
                            starredBy: data?.by_user,
                        }));
                    });
                    channel.listen('message-unstarred', (data) => {
                        dispatch(applyMessageUnstarred({
                            messageId: data?.message_id,
                        }));
                    });
                    channel.listen('message-pinned', (data) => {
                        dispatch(applyMessagePinned({
                            messageId: data?.message_id,
                            threadId: data?.thread_id,
                            pinnedMessage: { id: data?.message_id },
                            pinnedAt: new Date().toISOString(),
                        }));
                    });
                    channel.listen('message-unpinned', (data) => {
                        dispatch(applyMessageUnpinned({
                            threadId: data?.thread_id,
                        }));
                    });

                    // Track C / F6: a peer operator edited an outbound message.
                    // Apply the body swap surgically — no full thread refetch
                    // needed because the wamid + thread_id arrive in the
                    // payload. Mirrors the Track A reaction listener pattern.
                    const editedListener = (data) => {
                        const messageId = data?.message_id;
                        const newBody = data?.new_body;
                        if (!messageId || newBody == null) return;
                        dispatch(applyMessageEdited({
                            messageId,
                            newBody,
                            editedAt: data?.edited_at,
                        }));
                    };
                    channel.listen('message-edited', editedListener);

                    // Store everything needed for cleanup (Redux + local ref)
                    localSubs.push({ connection: pieSocketConn, roomId: jwtObj.roomId });
                    dispatch(updateSubChannel({
                        channel,
                        listener,
                        connection: pieSocketConn,
                        roomId: jwtObj.roomId,
                    }))
                });
            }
        });

        // Cleanup: unsubscribe all PieSocket connections opened by this effect run.
        // Fires on component unmount (logout / route away) and before each re-run
        // when deps change (practice switch). initializeWhatsapp() also calls
        // unsubscribeConnections() at the start of each init — calling unsubscribe
        // twice is safe (PieSocket treats it as a no-op on an already-closed channel).
        return () => {
            isCancelled = true;
            localSubs.forEach(({ connection, roomId }) => {
                try { connection.unsubscribe(roomId); } catch (_) { /* ignore */ }
            });
        };
    }, [businessIdToPhoneMap, currentPractice, dispatch, whatsappJwtTokens]);

    // Auth user has `pk`, not `id` — FCM's per-user de-dupe keyed on undefined made
    // every staff member on a shared PC look identical (RCA 2026-10-19).
    const fcmUserKey = userKeyOf(user);
    React.useEffect(() => {
        // Loading Merge Tags in First Load for use accross application
        if (currentPractice) {
            dispatch(fetchMergeTagKeys());
            dispatch(fetchMergeTagValueForPractice());
            dispatch(fetchPracticeUsers());
            dispatch(fetchMergeTagValueForUser());
            dispatch(resetChatData());
            dispatch(getAllTagItem(currentPractice, 30, 0));
            dispatch(selectLeads({ leadId: [], type: '', loading: false }));
            dispatch(getPracticeTablePreferences(currentPractice));
            // Deferred off the boot critical path — see fcmService.js. Cancelled
            // below on unmount / practice change so a stale timer never fires
            // a registration for a practice the user has since switched away from.
            const cancelFCMRegistration = scheduleFCMRegistration(currentPractice, fcmUserKey);
            listenForForegroundMessages(showCustomNotification);
            return () => {
                cancelFCMRegistration();
            };
        }
        return undefined;
    }, [dispatch, currentPractice, fcmUserKey]);

    React.useEffect(() => {
        if (!currentPractice) return;

        const currentPracticeKey = String(currentPractice);
        const permissionsReadyForPractice = (
            !permissionsLoading &&
            permissionsPracticeId != null &&
            String(permissionsPracticeId) === currentPracticeKey
        );
        const hasWhatsappMessagingAccess = (
            whatsappMessagingPermissionLevel === 'view' ||
            whatsappMessagingPermissionLevel === 'manage'
        );
        const alreadyInitializedForPractice = (
            whatsappInitializedForPracticeId != null &&
            String(whatsappInitializedForPracticeId) === currentPracticeKey
        );

        if (!permissionsReadyForPractice) return;
        if (!hasWhatsappMessagingAccess) return;
        if (whatsappIsLoading || alreadyInitializedForPractice) return;
        if (whatsappInitAttemptedPracticeRef.current === currentPracticeKey) return;

        whatsappInitAttemptedPracticeRef.current = currentPracticeKey;
        dispatch(initializeWhatsapp());
    }, [
        currentPractice,
        dispatch,
        permissionsLoading,
        permissionsPracticeId,
        whatsappInitializedForPracticeId,
        whatsappIsLoading,
        whatsappMessagingPermissionLevel,
    ]);

    // Prefetch all Google Reviews data when dashboard loads
    // This loads accounts, locations, and all review data so the page opens instantly.
    //
    // Waits until permissions have settled FOR THIS practice (same rule as the
    // WhatsApp init above). The thunk's hasFeatureAccess('google-reviews') gate
    // deliberately answers false while a practice switch is mid-flight (old
    // practice's snapshot, new practice loading). Dispatching on currentPractice
    // alone hit that window on every in-app switch, the thunk returned early, and
    // nothing re-ran it — multi-practice users saw "Connect Google Reviews" until
    // a hard refresh (RCA 2026-10-19, maddie@smilerooms.co.uk).
    // Remember this user's practice so the next sign-in reopens it (logout clears
    // the shared practiceId key). See src/utils/lastPractice.js.
    const lastPracticeUserKey = userKeyOf(user);
    React.useEffect(() => {
        if (currentPractice && lastPracticeUserKey != null) rememberLastPractice(lastPracticeUserKey, currentPractice);
    }, [currentPractice, lastPracticeUserKey]);

    const reviewsPrefetchedForPracticeRef = React.useRef(null);
    React.useEffect(() => {
        if (!currentPractice) return;
        const permissionsReadyForPractice = (
            !permissionsLoading &&
            permissionsPracticeId != null &&
            String(permissionsPracticeId) === String(currentPractice)
        );
        if (!permissionsReadyForPractice) return;
        // Once per practice, as before — a same-practice permissions refresh must
        // not replay the whole Google Reviews prefetch.
        if (reviewsPrefetchedForPracticeRef.current === String(currentPractice)) return;
        reviewsPrefetchedForPracticeRef.current = String(currentPractice);
        dispatch(prefetchGoogleReviewsOnDashboardLoad(currentPractice));
    }, [dispatch, currentPractice, permissionsLoading, permissionsPracticeId]);

    // Global keyboard shortcut for command palette (⌘K / Ctrl+K)
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setCommandPaletteOpen(true);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);


    // P2-2 I1: single top-level mount of TaskUndoSnackbarHost so every V2
    // task surface (Dashboard / TaskDashboard / LeadTaskList) shares one
    // window-event listener. popUndo's drain semantics already keep
    // multiple hosts safe; this removes the listener fan-out + ensures the
    // Snackbar appears regardless of which V2 surface triggered completion.
    const renderUndoHost = currentPractice
        ? <TaskUndoSnackbarHost practiceId={currentPractice} />
        : null;

    // Attendance pop-ups exist only for practices with attendance capture enabled
    // (PracticePreferences.attendance_capture_eligible); the capture helpers also
    // short-circuit to the legacy requests for other practices, so this host would
    // never open there — hidden explicitly all the same.
    // HOTFIX 2026-10-28: the host must ALWAYS mount. Besides rendering the pop-ups it
    // registers the practice scope the capture helpers check on EVERY path, including
    // the legacy one; gating it on the flag (#4771) left the scope unset and 'Start
    // treatment' / 'Mark attended' failed in production with 'Attendance is unavailable
    // for this practice'. The flag still decides whether a dialog can ever open
    // (the helpers short-circuit to legacy requests for ineligible practices).
    const renderAttendanceHost = <AttendanceCaptureHost scope={attendanceScopeForUser(currentPractice, practiceSwitchEpoch, user, attendanceSessionKey)} />;

    // Keying the routed page on the practice-switch epoch gives it a real
    // unmount/remount when the header switcher keeps the operator on the same
    // route. React Router alone would not remount here — the matched route is
    // unchanged (and for /practice/:practiceId/* URLs only a param changes), so
    // a page that fetches on mount would keep rendering the previous practice's
    // data. The epoch is bumped only by user-initiated switches, so the
    // URL->Redux syncs done by Whatsapp/LeadDetail/PracticeDetails do not
    // trigger a spurious remount. See practiceTreatmentList.practiceSwitchEpoch.
    const routedPage = <Outlet key={`practice-switch-${practiceSwitchEpoch ?? 0}`} />;

    if (horizontalLayout) {
        return (
            <TwilioVoiceProvider practiceId={currentPractice}>
                <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
                <DashboardHeader onOpenSidebar={handleOpen} onOpenCommandPalette={() => setCommandPaletteOpen(true)} />
                <CreateLeadDialogWrapper />
                <ContactLeadPopupSingleton />
                {renderUndoHost}
                {renderAttendanceHost}

                {isDesktop ? <NavbarHorizontal /> : renderNavVertical}

                <Main>
                    {routedPage}
                </Main>
            </TwilioVoiceProvider>
        );
    }

    if (isNavMini) {
        return (
            <TwilioVoiceProvider practiceId={currentPractice}>
                <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
                <DashboardHeader onOpenSidebar={handleOpen} onOpenCommandPalette={() => setCommandPaletteOpen(true)} />
                <CreateLeadDialogWrapper />
                <ContactLeadPopupSingleton />
                {renderUndoHost}
                {renderAttendanceHost}

                <Box
                    sx={{
                        display: { lg: 'flex' },
                        minHeight: { lg: 1 },
                    }}
                >
                    {isDesktop ? <NavMini /> : renderNavVertical}

                    <Main>
                        {routedPage}
                    </Main>
                </Box>
            </TwilioVoiceProvider>
        );
    }

    return (
        <TwilioVoiceProvider practiceId={currentPractice}>
            <CommandPalette open={commandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />
            <DashboardHeader onOpenSidebar={handleOpen} onOpenCommandPalette={() => setCommandPaletteOpen(true)} />
            <CreateLeadDialogWrapper />
            <ContactLeadPopupSingleton />
            <EnableNotificationsDialog />
            {renderUndoHost}
                {renderAttendanceHost}
            <Box
                sx={{
                    display: { lg: 'flex' },
                    minHeight: { lg: 1 },
                }}
            >
                {renderNavVertical}

                <Main>
                    {routedPage}
                </Main>
            </Box>
        </TwilioVoiceProvider>
    );
}
